/* eslint-disable import/no-default-export */
import { HeaderTestIds } from '@trello/webdriverio-tests';
import classNames from 'classnames';
import { IconName } from 'app/common/components/controls/icon';
import { Noun, Verb, trackUe } from '@trello/analytics';
import HeaderButton from 'app/gamma/src/components/header/button';
import { State } from 'app/gamma/src/modules/types';
import {
  loadNotificationGroups,
  loadUnreadNotificationsCount,
} from 'app/gamma/src/modules/loaders/load-notifications';
import React, {
  Suspense,
  FunctionComponent,
  useEffect,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'app/gamma/src/types';
import FavIcon from 'app/scripts/lib/favicon.coffee';
import { memberHasOtherNotifications } from 'app/gamma/src/selectors/members';
import { getUnseenNotificationsCount } from 'app/gamma/src/selectors/notifications';
import styles from './styles.less';
import { usePopover, Popover } from '@trello/nachos';
import { forTemplate } from 'app/common/lib/util/i18n';
import { useLazyComponent } from '@trello/use-lazy-component';
const formatHeaderUser = forTemplate('header_user');

const NOTIFICATIONS_POPOVER_WIDTH = 432;

interface NotificationsMenuButtonProps {
  onLoadNotificationGroups: () => void;
  onLoadUnreadNotificationsCount: () => void;
  hasOtherNotifications: boolean;
  unreadNotificationsCount: number;
}

export const NotificationsMenuButtonUnconnected: FunctionComponent<NotificationsMenuButtonProps> = ({
  onLoadNotificationGroups,
  onLoadUnreadNotificationsCount,
  hasOtherNotifications,
  unreadNotificationsCount,
}) => {
  const HeaderNotificationsMenu = useLazyComponent(() =>
    import(
      /* webpackChunkName: "notifications-menu-popover" */ 'app/gamma/src/components/popovers/header-notifications-menu'
    ),
  );

  useEffect(() => {
    onLoadUnreadNotificationsCount();
    onLoadNotificationGroups();
  }, [onLoadNotificationGroups, onLoadUnreadNotificationsCount]);

  useEffect(() => {
    if (!hasOtherNotifications && unreadNotificationsCount > 0) {
      FavIcon.setNotifications(true);
    }

    if (!hasOtherNotifications && unreadNotificationsCount === 0) {
      FavIcon.setNotifications(false);
    }
  }, [hasOtherNotifications, unreadNotificationsCount]);

  const onShow = useCallback(() => {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.NOTIFICATIONS_MENU,
      method: 'by clicking the header button',
    });
  }, []);

  const onHide = useCallback(() => {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.CLOSES,
      directObj: Noun.NOTIFICATIONS_MENU,
      method: 'by clicking the header button',
    });
  }, []);

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>({
    onShow,
    onHide,
  });

  return (
    <>
      <HeaderButton
        icon={IconName.Notification}
        onClick={toggle}
        ref={triggerRef}
        testId={HeaderTestIds.NotificationsButton}
        className={classNames({
          [styles.newNotifications]:
            !hasOtherNotifications && unreadNotificationsCount > 0,
          [styles.hasOtherNotifications]: hasOtherNotifications,
        })}
      />
      <Popover
        {...popoverProps}
        title={formatHeaderUser('notifications-menu')}
        noHorizontalPadding
        testId={HeaderTestIds.NotificationsPopover}
        dangerous_width={NOTIFICATIONS_POPOVER_WIDTH}
      >
        <Suspense fallback={null}>
          <HeaderNotificationsMenu />
        </Suspense>
      </Popover>
    </>
  );
};

const mapStateToProps = (state: State) => {
  return {
    unreadNotificationsCount: getUnseenNotificationsCount(state),
    hasOtherNotifications: memberHasOtherNotifications(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onLoadNotificationGroups() {
      dispatch(loadNotificationGroups());
    },
    onLoadUnreadNotificationsCount() {
      dispatch(loadUnreadNotificationsCount());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NotificationsMenuButtonUnconnected);
